import { useEffect } from 'react';
import { connect } from 'react-redux';

import moment from 'moment';
import PropTypes from 'prop-types';
import { change, getFormValues } from 'redux-form';

import { CircularLoaderComponent } from '../LoadingSpinner/LoadingSpinnerComponent';
import AccessCardFormComponent from './AccessCardFormComponent';

import {
  getBuildingsForFilter,
  getPeopleInBuilding,
} from '../Building/actionCreators';
import { getPersons } from '../User/actions';
import {
  createAccessCard,
  getAccessCard,
  getAccessCardsList,
  updateAccessCard,
  validateAccessCard,
} from './actions';

import { formatSelect } from '../Forms/utils';
import { getAllPersonRoles } from '../Person/utils';
import {
  getPersonOptions,
  getPersonProperties,
  getResidentProperties,
  isPersonOrPropertyChanged,
} from './utils';

const form = 'accessCard';

const AccessCardFormContainer = (props) => {
  const {
    onLoad,
    isCreate,
    initialValues,
    selectedBuildingId,
    preselectedBuilding,
    onBuildingChange,
    selectedType,
    onGetBuildingsForFilter,
  } = props;

  const isLyazonType = selectedType === 'lyazon';

  useEffect(() => {
    onLoad(props);
    preselectedBuilding && onBuildingChange(preselectedBuilding);
  }, []);

  useEffect(() => {
    selectedBuildingId && onBuildingChange(selectedBuildingId);
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedType) {
      isLyazonType
        ? onGetBuildingsForFilter({
            associatedWithLyazon: true,
          })
        : onGetBuildingsForFilter({
            associatedWithTwoN: true,
          });
    }
  }, [selectedType]);

  if (!isCreate && !initialValues?.id) {
    return <CircularLoaderComponent />;
  }

  return (
    <AccessCardFormComponent
      {...props}
      isLyazonType={isLyazonType}
      selectedType={selectedType}
    />
  );
};

AccessCardFormContainer.defaultProps = {
  selectedBuildingId: null,
  preselectedBuilding: null,
  selectedType: null,
};
AccessCardFormContainer.propTypes = {
  onLoad: PropTypes.func.isRequired,
  isCreate: PropTypes.bool.isRequired,
  initialValues: PropTypes.instanceOf(Object).isRequired,
  selectedBuildingId: PropTypes.number,
  onBuildingChange: PropTypes.func.isRequired,
  preselectedBuilding: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  selectedType: PropTypes.string,
  onGetBuildingsForFilter: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { match, location } = ownProps;
  const {
    accessCards: { card, batch },
    buildings,
  } = state;

  const accessCardId = match?.params?.id;
  const isCreate = Number.isNaN(Number(accessCardId));

  const preselectedBuilding =
    match?.params?.buildingId || location?.state?.buildingId;
  const preselectedPerson = location?.state?.id && location?.state;

  const preselectedProperties = getAllPersonRoles(preselectedPerson).map(
    ({ propertyId }) => propertyId,
  );
  const properties = card?.properties || [...new Set(preselectedProperties)];

  const initialValues = {
    effectiveDate: moment().format(),
    building: preselectedBuilding,
    ...card,
    creator: card?.creator?.id,
    person: card?.person?.id || preselectedPerson?.id,
    properties,
  };

  const formValues = getFormValues(form)(state);

  const selectedBuildingId = formValues?.building;
  const buildingPersons = buildings?.[selectedBuildingId]?.people;
  const personOptions = getPersonOptions(buildingPersons);

  const selectedPersonId = formValues?.person;
  const selectedPerson =
    buildingPersons?.find((person) => person.id === selectedPersonId) ||
    preselectedPerson;

  const isRegistered = selectedPerson
    ? selectedPerson?.registered ?? selectedPerson?.editable === false
    : true;
  const hasVerifiedPhoneNumber = selectedPerson?.contactInfo?.find(
    (contact) => contact.type === 'phone_number',
  )?.verified;

  const personsProperties = preselectedPerson
    ? getResidentProperties(preselectedPerson)
    : getPersonProperties(selectedPersonId, buildingPersons);

  const selectedBatch = formValues?.accessCardBatch;
  const batchOptions = [];
  if (Array.isArray(batch)) {
    batch.forEach((item) =>
      batchOptions.push({
        ...item,
        name: item.name,
      }),
    );
  }
  const selectedType = batchOptions?.find(
    (batch) => batch.id === selectedBatch,
  )?.type;
  const propertyId = batchOptions?.find(
    (batch) => batch.id === selectedBatch,
  )?.property;
  const filteredBuilding = propertyId
    ? {
        ...Object.values(buildings)?.filter(
          (building) =>
            building.id === propertyId || building?.parent?.id === propertyId,
        ),
      }
    : buildings;
  initialValues.type = selectedType;

  return {
    form,
    initialValues,
    preselectedPerson,
    personsProperties,
    selectedPersonId,
    selectedPerson,
    isRegistered,
    hasVerifiedPhoneNumber,
    batchOptions: formatSelect(batchOptions, 'name', 'id'),
    batch,
    selectedBatch,
    isCreate,
    buildings: filteredBuilding,
    selectedBuildingId,
    preselectedBuilding,
    personOptions,
    selectedType,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const accessCardId = ownProps?.match?.params?.id;
  const isCreate = Number.isNaN(Number(accessCardId));
  return {
    onLoad: async () => {
      getAccessCardsList(dispatch, { disable_pagination: true }, true);
      if (!isCreate) {
        const card = await getAccessCard(dispatch, accessCardId);
        const properties = card?.data?.properties;
        const personId = card?.data?.person?.id;
        if (personId) {
          const queryParams = {
            disable_pagination: true,
            id: personId,
          };
          const persons = await getPersons(dispatch, queryParams);
          persons?.data && dispatch(change(form, 'properties', properties));
        }
      }
    },
    onGetBuildingsForFilter: (params) =>
      getBuildingsForFilter(dispatch, params),
    onSubmit: async (data, props) => {
      const { id, accessCardBatch, person, properties } = data;
      const { initialValues, batch } = props;

      const batchType = batch?.find(({ id }) => id === accessCardBatch)?.type;

      const updatedData = {
        ...data,
        type: initialValues?.type,
      };

      const shouldValidate =
        batchType !== 'lyazon' &&
        (isCreate ||
          isPersonOrPropertyChanged(updatedData, initialValues, isCreate));

      const isValidData = shouldValidate
        ? await validateAccessCard(dispatch, {
            person,
            properties,
          })
        : true;

      if (!isValidData) return null;

      return id
        ? updateAccessCard(dispatch, id, updatedData)
        : createAccessCard(dispatch, updatedData);
    },
    onBuildingChange: (buildingId) => getPeopleInBuilding(dispatch, buildingId),
    getPersons: (queryParams) => getPersons(dispatch, queryParams),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccessCardFormContainer);
